'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');

let measure = performanceMeasure('webcomponents:balloonHeader');
const ACTIVE = 'is-active';
const POPPED = 'is-popped';
const IS_BALLOON_ACTIVE = 'is-balloon-header-active';
// We've determined that 3.5 seconds is as low as we think we can go without
// affecting visibility; see ADS-3521.
const DEFAULT_POP_TIME = 3.5 * 1000;
const componentTag = 'phoenix-balloon-header';

/**
 * Phoenix Balloon Header -- a wrapper for (above-the-fold) header ads
 * to improve visibility. Until the timer "pops", the header will be
 * fixed-position with top: 0. The default time-to-pop is optionally
 * overrideable (with a float representing seconds).
 *
 * @example
 *      <phoenix-balloon-header class="m-balloon-header" pop-time="2.5">
 *          <div class="m-header-ad m-header-ad--balloon" ...>
 *     </phoenix-balloon-header>
 *
 */

getDependencyClass('phoenix-page').then((pageClass) => {
    class PhoenixBalloonHeader extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return ['phoenix:breakpoint:changed'];
        }

        connectedCallback() {
            super.connectedCallback();
            this.active = false;
            this.hover = false;
            this.docBody = document.querySelector('body');
            this.pageWrapper = document.querySelector('.m-page-wrapper');
            this.adContainer = this.firstElementChild;
            this.isHeaderAdBelowNav = !!this.querySelector('.is-below-header-ad');
            if (this.isHeaderAdBelowNav) {
                this.fillerContainer = this.nextElementSibling;
            }
            this.popTime = isNaN(parseFloat(this.getAttribute('pop-time')))
                ? DEFAULT_POP_TIME
                : parseFloat(this.getAttribute('pop-time')) * 1000;
            this.hasLegacyNav = !!this.querySelector('.has-legacy-nav');
            if (!this.pageWrapper || !this.adContainer) {
                return;
            }

            // Make certain the current browser breakpoint (A/B/C/D) is
            // compatible with the classes assigned to the ad container
            // element (ie: the ad container should not be rendered for
            // breakpoint A should not be if it is assigned a 'not-size-a'
            // class).
            if (
                !this.adContainer.classList.contains(
                    `not-size-${pageClass.util.getCurrentBreakpoint().toLowerCase()}`
                )
            ) {
                this.canRender().then((canRender) => {
                    if (canRender) {
                        this.init();
                    } else {
                        // For CLS reasons we aren't collapsing the container if demonetized.
                        // See: https://mavencorp.atlassian.net/browse/ADS-4643.
                        this.classList.remove(ACTIVE);
                        this.docBody.classList.remove(IS_BALLOON_ACTIVE);
                    }
                });
            }
        }

        init() {
            // Activate early (and return without activating if we don't
            // get a fill--this is an important distinction to avoid
            // accidentally triggering any listeners for the popped event) to
            // ensure that lazy loading doesn't keep our balloon header from
            // ever rendering if we start the viewport down the page.
            this.activate();

            // As we do in stickyNav, we'll set up our listener based on the
            // advertising queue, rather than create a more expensive Mutation
            // Observer.
            const self = this;
            let queue = window.phxAdvertisingQueue || [];
            queue.push(function () {
                let adSlot = self.querySelector('.m-header-ad [id^="ad-"]');
                if (adSlot) {
                    const slotId = adSlot.id;
                    this.getSlotResult(slotId).then(function (result) {
                        if (result.filled) {
                            // We've already activated (visually); set up
                            // listeners/event handlers now.
                            self.listen();
                            // Make sure the height wasn't adjusted by the
                            // ad fill before the listeners were set up.
                            self._updateHeight();
                        } else {
                            self.deactivate();
                        }
                    });
                }
            });
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.deactivate();
        }

        broadcastedEventCallback(name, args = {}) {
            if (name === 'phoenix:breakpoint:changed' && this.active) {
                this.deactivate();
            }
        }

        activate() {
            if (!this.active) {
                this.active = true;
                this._updateHeight();
                this.classList.add(ACTIVE);
                this.docBody.classList.add(IS_BALLOON_ACTIVE);
                this.emitEvent('phoenix:balloonHeader:activated', {
                    bubbles: true,
                    detail: {},
                });
            }
        }

        listen() {
            // We want to hold space for the header ad at the
            // top of the page. Unfortunately, the header ad is
            // a bit "bouncy"--there's often a jump from
            // a placeholder size (62px) to the real size after
            // the load event. The load event is detectable in
            // the advertising queue, but we can't easily
            // detect when the ad has reached its final size.
            // As such, we'll tweak the wrapper height as that
            // value is adjusted.
            let updateHeight = this._updateHeight.bind(this);
            this.heightObserver = new MutationObserver(updateHeight);
            this.heightObserver.observe(this.adContainer, {
                attributes: true,
                childList: true,
                subtree: true,
            });

            this.addEventListener('mouseover', this._mouseover, false);
            this.addEventListener('mouseout', this._mouseout, false);

            let delayedPop = () => {
                // We don't want to pop the balloon in the
                // middle of a user interaction.
                if (this.hover) {
                    window.setTimeout(delayedPop, this.popTime);
                } else {
                    this.deactivate();
                }
            };
            window.setTimeout(delayedPop, this.popTime);
        }

        deactivate() {
            if (this.active) {
                this.active = false;
                if (this.heightObserver) {
                    this.heightObserver.disconnect();
                }
                if (this.fillerContainer) {
                    this.fillerContainer.classList.add(POPPED);
                }
                this.classList.remove(ACTIVE);
                this.docBody.classList.remove(IS_BALLOON_ACTIVE);
                if (!this.isHeaderAdBelowNav) {
                    this.pageWrapper.style.marginTop = '0';
                }
                this._updateHeight();
                this.removeEventListener('mouseover', this._mouseover);
                this.removeEventListener('mouseout', this._mouseout);
                this.emitEvent('phoenix:balloonHeader:popped', {
                    bubbles: true,
                    detail: {},
                });
            }
        }

        _mouseover() {
            this.hover = true;
        }

        _mouseout() {
            this.hover = false;
        }

        _updateHeight() {
            // Note that this is expensive! We only will do this a couple of
            // times, as the ad loads. It's probably going to be around 130px
            // when it settles down, but the header ad could be larger.
            //
            // We don't want to jump *down* to the placeholder size, then
            // immediately jump back up -- that leads to the content being
            // jittery. The goal here is to expand up to the final size of the
            // ad; as such, we'll only get bigger until the balloon pops.
            //
            // Note that currently we don't have a setup which would mandate
            // that we go *down* in size from the placeholder. If that occurs
            // we'll need to update the logic, although hopefully by then we'll
            // have a more direct way of ascertaining the header height when
            // the ad fills.
            // let oldHeight = this.height;
            let { height } = this.adContainer.getBoundingClientRect();
            this.height = height;
            if (this.isHeaderAdBelowNav) {
                // Only expand the size of the filler, never make it smaller.
                // This means if no ad fills there will be an empty space that
                // will still show. We currently want to avoid the CLS that
                // would result from collapsing it
                if (height > this.fillerContainer.clientHeight) {
                    this.fillerContainer.style.height = height + 'px';
                }
            } else if (!this.hasLegacyNav) {
                this.pageWrapper.style.marginTop = `${this.active ? this.height : 0}px`;
            }
            this._updateBalloonHeaderVar();
        }

        _updateBalloonHeaderVar() {
            // We use a CSS var to dynamically adjust the top positioning on
            // elements other than the page wrapper. (In practice, this means
            // the JWPlayer float-and-park div, which cannot be directly
            // targeted because it may not be on the page when the balloon
            // header activates.) The page wrapper is adjusted directly to
            // provide the broadest support for legacy browsers.
            if (this.adContainer.classList.contains('is-empty')) {
                this.height = 0;
            }

            document
                .querySelector('html')
                .style.setProperty('--balloon-header-height', `${this.height || 0}px`);
            this.emitEvent('phoenix:balloonHeader:heightChanged', {
                bubbles: true,
            });
        }
    }

    window.customElements.define(componentTag, PhoenixBalloonHeader);
    measure.end();
});
